import { FC, useMemo } from 'react'

import { Anchor, FontWeight } from '@hhgtech/hhg-components/mantine'
import { ChevronRight } from '@hhgtech/icons/arrow'
import { Box, rem, SimpleGrid } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import FeatureCard from 'components/molecules/FeatureCard'
import { FeatureCardLayout } from 'components/molecules/FeatureCard/types'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { MedicalReviewApi } from 'interfaces/types'

import { useHomeTrustful } from './HomeTrustful.hooks'
import {
  StyledHomeTrustfulItem,
  StyledHomeTrustfulItemContent,
  StyledHomeTrustfulItemDescription,
  StyledHomeTrustfulItemIcon,
  StyledHomeTrustfulItemLabel,
  StyledHomeTrustfulModal,
  StyledHomeTrustfulTitle,
  StyledHomeTrustfulTitleAvatar,
} from './HomeTrustful.styled'
import { TrustfulItem } from './TrustfulItem'

export type HomeTrustfulProps = {
  medicalReviewed?: MedicalReviewApi[]
}
export const HomeTrustful: FC<HomeTrustfulProps> = (props) => {
  const { medicalReviewed: medicalReviewedProps = [] } = props
  const [opened, { open, close }] = useDisclosure(false)
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const { trustfulItems } = useHomeTrustful({
    medicalReviewed: medicalReviewedProps,
  })
  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  if (trustfulItems.length === 0) return null

  return (
    <>
      <Box
        sx={(theme) => ({
          position: 'relative',
          [theme.fn.smallerThan('sm')]: {
            backgroundColor: '#fff',
            paddingTop: 16,
          },
        })}
      >
        <Anchor
          href="#"
          onClick={(e) => {
            e.preventDefault()
            open()
          }}
          className="homeTrustfulButtonMore"
          data-event-category="Commitment"
          data-event-action="learn about"
          data-event-label={`${currentDomain}${basePath}`}
          sx={(theme) => ({
            [theme.fn.largerThan('sm')]: {
              display: 'none',
            },
            [theme.fn.smallerThan('sm')]: {
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'flex',
              alignItems: 'center',
              gap: rem(4),
            },
          })}
        >
          <Box
            component="span"
            sx={{
              fontSize: rem(14),
              lineHeight: rem(22),
              letterSpacing: '-0.2px',
            }}
          >
            {f({
              id: 'home.trustful.buttonMore',
              defaultMessage: 'Tìm hiểu',
            })}
          </Box>
          <ChevronRight size={16} color="currentColor" />
        </Anchor>
        <FeatureCard
          layout={FeatureCardLayout.VerticalCenter}
          styles={(theme) => ({
            header: {
              [theme.fn.smallerThan('sm')]: {
                paddingTop: 0,
                flexDirection: 'row',
              },
            },
            inner: {
              [theme.fn.smallerThan('sm')]: {
                gap: 10,
              },
            },
          })}
        >
          <FeatureCard.Heading
            sx={(theme) => ({
              [theme.fn.smallerThan('sm')]: {
                fontSize: rem(14),
                lineHeight: rem(22),
                fontWeight: FontWeight.semiBold,
                letterSpacing: 0,
              },
            })}
          >
            {f({
              id: 'home.trustful.title',
              defaultMessage: 'Cam kết thông tin',
            })}
          </FeatureCard.Heading>
          <FeatureCard.Icon src="/images/hhg-logo.png" />
          <FeatureCard.Content
            sx={(theme) => ({
              [theme.fn.smallerThan('sm')]: {
                paddingLeft: 24,
                paddingRight: 24,
              },
              [theme.fn.largerThan('sm')]: {
                paddingBottom: 40,
              },
            })}
          >
            <SimpleGrid
              cols={1}
              spacing={0}
              verticalSpacing={0}
              breakpoints={[
                {
                  minWidth: 'sm',
                  cols: trustfulItems.length > 5 ? 5 : trustfulItems.length,
                  spacing: 24,
                  verticalSpacing: 24,
                },
              ]}
            >
              {trustfulItems?.map((trustfulItem, idx) => {
                return (
                  <Box
                    sx={{ flex: '0 0 calc(20% - 19.2px)' }}
                    key={String(idx)}
                  >
                    <TrustfulItem
                      title={trustfulItem.title}
                      icon={trustfulItem.icon}
                      onClick={() => open()}
                      className="pointer-event-child-none"
                      data-event-category="Commitment"
                      data-event-action="learn about"
                      data-event-label={`${currentDomain}${basePath}`}
                      sx={(theme) => ({
                        [theme.fn.smallerThan('sm')]: {
                          borderBottom: `1px solid ${theme.colors.neutral[0]}`,
                        },
                      })}
                    />
                  </Box>
                )
              })}
            </SimpleGrid>
          </FeatureCard.Content>
        </FeatureCard>
      </Box>
      <StyledHomeTrustfulModal
        centered
        opened={opened}
        title={
          <StyledHomeTrustfulTitle className="homeTrustfulTitle">
            <StyledHomeTrustfulTitleAvatar
              src="/images/hhg-logo.png"
              size={rem(16)}
              radius={0}
              className="homeTrustfulTitleIcon"
            />
            <span className="homeTrustfulTitleLabel">
              {f({
                id: 'home.trustful.title',
                defaultMessage: 'Cam kết thông tin',
              })}
            </span>
          </StyledHomeTrustfulTitle>
        }
        onClose={close}
      >
        {trustfulItems?.map((trustfulItem, idx) => {
          return (
            <StyledHomeTrustfulItem className="homeTrustfulItem" key={`${idx}`}>
              <StyledHomeTrustfulItemIcon
                src={trustfulItem.icon}
                className="homeTrustfulItem"
              />
              <StyledHomeTrustfulItemContent className="homeTrustfulItemContent">
                <StyledHomeTrustfulItemLabel>
                  {trustfulItem.title}
                </StyledHomeTrustfulItemLabel>
                <StyledHomeTrustfulItemDescription className="homeTrustfulItemDescription">
                  {trustfulItem.description}
                </StyledHomeTrustfulItemDescription>
              </StyledHomeTrustfulItemContent>
              <StyledHomeTrustfulItemDescription className="homeTrustfulItemDescription">
                {trustfulItem.description}
              </StyledHomeTrustfulItemDescription>
            </StyledHomeTrustfulItem>
          )
        })}
      </StyledHomeTrustfulModal>
    </>
  )
}
